import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const TimeoutConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3">
      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Timeout Beállítások"
        icon="⏱️"
        color="orange"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Időtúllépés (másodperc)
            </label>
            <input
              type="number"
              value={node.data.config.timeoutSeconds || 30}
              onChange={(e) => updateConfig('timeoutSeconds', parseInt(e.target.value))}
              className={commonStyles.input}
              min="1"
              max="3600"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum várakozási idő (1-3600 másodperc)
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => updateConfig('timeoutSeconds', 10)}
              className="px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              10s
            </button>
            <button
              onClick={() => updateConfig('timeoutSeconds', 30)}
              className="px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              30s
            </button>
            <button
              onClick={() => updateConfig('timeoutSeconds', 60)}
              className="px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              60s
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="throwOnTimeout"
              checked={node.data.config.throwOnTimeout || false}
              onChange={(e) => updateConfig('throwOnTimeout', e.target.checked)}
              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
            />
            <label htmlFor="throwOnTimeout" className="text-sm text-gray-700 dark:text-gray-300">
              Hiba dobása timeout esetén
            </label>
          </div>

          <div>
            <label className={commonStyles.label}>
              Timeout üzenet
            </label>
            <input
              type="text"
              value={node.data.config.timeoutMessage || ''}
              onChange={(e) => updateConfig('timeoutMessage', e.target.value)}
              className={commonStyles.input}
              placeholder="Művelet időtúllépés - {{timeoutSeconds}}s"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Egyéni timeout üzenet
            </p>
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
            <div className="text-xs text-orange-700 dark:text-orange-300">
              <strong>💡 Működés:</strong> A Timeout node két kimenetre irányít: <code className="bg-orange-100 dark:bg-orange-800 px-1 rounded">completed</code> (időben befejeződött) és <code className="bg-orange-100 dark:bg-orange-800 px-1 rounded">timeout</code> (időtúllépés).
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A timeout ellenőrzés után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{result}}', desc: 'Eredmény', example: '{...}', output: 'completed' },
              { var: '{{duration}}', desc: 'Futási idő (ms)', example: '1500', output: 'both' },
              { var: '{{timedOut}}', desc: 'Timeout történt-e', example: 'true/false', output: 'both' },
              { var: '{{timeoutMessage}}', desc: 'Timeout üzenet', example: 'Timeout after 30s', output: 'timeout' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {item.output}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🌐 API timeout:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Timeout → HTTP Request → [completed] Success<br/>
              Timeout → [timeout] Fallback Action
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🗄️ Database timeout:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Timeout → Database Query → [timeout] Error Logger
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>⏳ Hosszú művelet:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Timeout (60s) → File Processing → [completed] Continue
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default TimeoutConfig;
